import React, { useCallback, useContext, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { FormikContext } from "formik";
import DoubleContent from "../common/DoubleContent";
import Headline from "../common/Headline";
import { SectionContent } from "../common/Section/Section";
import Text from "../common/Text";
import Link from "../common/Link";
import SwitchContent from "../common/SwitchContent";
import SelectControl from "../common/Forms/SelectControl";
import InputControl from "../common/Forms/InputControl";
import { ButtonPrimary } from "../common/Buttons";
import Loading from "../common/Loading";
import ResultadoDeSorteiosForm from "../../forms/ResultadoDeSorteios";
import { useResultadoSorteios } from "../../hooks";
import * as S from "./ResultadoSorteiosCapitalizacao.Style";
import { TITULO_PATTERN } from "../../hooks/requests/useResultadoSorteios";
import PainelResultados from "./PainelResultados";
import { links } from "../../config";
import { ReactComponent as IlustracaoContempladoSm } from "../../assets/images/capitalizacao/ilustracao-contemplacao-resultado-sorteios-mobile.svg";
import { ReactComponent as IlustracaoContempladoLg } from "../../assets/images/capitalizacao/ilustracao-contemplacao-resultado-sorteios-desktop.svg";

const consultaPorTitulo = (
  <>
    <S.LabelInputTitulo textSize="md">Número do título</S.LabelInputTitulo>
    <InputControl placeholder="Apenas números" name="titulo" />
  </>
);

const FormConsulta = (props) => {
  const { resultados, dataSorteios, plano, products, children } = props;

  const { setFieldValue, values, isValid, isSubmitting, touched } = useContext(
    FormikContext
  );

  const productsOptions = useMemo(
    () => products.map(({ label }, value) => ({ label, value })),
    [products]
  );

  const setTipoBuscaHandle = useCallback(
    (tipo) => setFieldValue("tipoDeBusca", tipo, false),
    [setFieldValue]
  );

  const getProduct = useCallback(
    (itens, index) => (index ? itens[index] : values.produto),
    [values.produto]
  );

  const consultaPorProduto = (
    <SelectControl
      options={productsOptions}
      name="produtoAux"
      placeholder="Selecione um produto"
    />
  );

  useEffect(() => {
    const tituloReplaced = values.titulo?.replace(/\D/gi, "");
    setFieldValue("titulo", tituloReplaced.substring(0, 14), touched.titulo);
  }, [setFieldValue, touched.titulo, values.titulo]);

  useEffect(() => {
    if (values.tipoDeBusca === "titulo") {
      const planoId = Number.parseFloat(
        values.titulo?.replace(TITULO_PATTERN, (...[, produto, variacao]) => {
          const planoOnlyNumbers = Number.parseInt(produto, 10);
          const variacaoOnlyNumbers = Number.parseInt(variacao, 10);

          return `${planoOnlyNumbers}.${variacaoOnlyNumbers}`;
        }),
        10
      );

      const produtoEncontrado = products.find(
        ({ value: productValue }) => planoId === productValue
      );

      setFieldValue("produto", produtoEncontrado, touched.titulo);
    }
  }, [
    products,
    setFieldValue,
    touched.titulo,
    values.tipoDeBusca,
    values.titulo,
  ]);

  useEffect(() => {
    if (values.tipoDeBusca === "produto") {
      setFieldValue("titulo", "", false);
    }
  }, [setFieldValue, values.tipoDeBusca]);

  useEffect(() => {
    const produto = getProduct(products, values.produtoAux);

    setFieldValue("produto", produto);
  }, [getProduct, products, setFieldValue, values.produtoAux]);

  return (
    <>
      {!resultados ? (
        <DoubleContent
          collumnSize={7}
          leftContent={
            <>
              <Headline styleAs="5" nivel="2">
                Será que hoje é o seu dia de sorte?
              </Headline>
              <Text>
                Você pode visualizar os resultados dos sorteios pelo número do
                seu título para descobrir se tem algum prêmio a receber. Se
                preferir, pode consultar pelo nome do seu título para saber
                quais números foram sorteados.
              </Text>
              {children}
            </>
          }
          rightContent={
            <>
              {values.tipoDeBusca === "titulo" && (
                <>
                  <S.LabelText textType="caption">
                    Como você prefere consultar o resultado:
                  </S.LabelText>
                  <SwitchContent
                    leftButton="Número do título"
                    rightButton="Nome do produto"
                    leftId="titulo"
                    rightId="produto"
                    leftContent={consultaPorTitulo}
                    rightContent={consultaPorProduto}
                    contentActive={values.tipoDeBusca}
                    handleChange={setTipoBuscaHandle}
                  />{" "}
                </>
              )}

              {consultaPorProduto}
              <S.ConsultaButton
                as={ButtonPrimary}
                disabled={!isValid || isSubmitting}
                bSize="md"
                elementType="input"
                type="submit"
                label="Consultar"
              />

              <S.NumeroTituloText as={Text} textSize="sm">
                Não sabe qual é o número do seu título?
              </S.NumeroTituloText>
              <S.LinkGuia
                as={Link}
                textSize="sm"
                href={links.capitalizacao.guiaSorteios}
                target="_blank"
                label="Veja aqui o nosso guia"
              />
            </>
          }
        />
      ) : (
        <PainelResultados
          resultados={resultados}
          datasSorteios={dataSorteios}
          plano={plano}
        />
      )}
    </>
  );
};

FormConsulta.propTypes = {
  resultados: PropTypes.oneOfType([PropTypes.object]),
  dataSorteios: PropTypes.arrayOf(PropTypes.string),
  plano: PropTypes.shape({
    label: PropTypes.string,
  }),
  products: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
};

FormConsulta.defaultProps = {
  resultados: null,
  dataSorteios: [],
  plano: null,
};

const ResultadoSorteiosCapitalizacao = (props) => {
  const {
    setBusca,
    resultados,
    dataSorteios,
    plano,
    contemplado,
    titulo: tituloBuscado,
    loading,
  } = useResultadoSorteios();

  const buscarResultadoHandle = useCallback(
    ({ titulo, produto, dataBusca }) => {
      setBusca({ plano: produto, dataBusca, titulo });
    },
    [setBusca]
  );

  const statusContemplacaoContent = useMemo(
    () =>
      tituloBuscado && contemplado ? (
        <S.ContentContemplado>
          <div>
            <Headline
              nivel="5"
              text="Parabéns! Seu título foi contemplado ;)"
            />
            <Text>
              Para saber como receber o seu prêmio, ligue:{" "}
              <Link href="tel:0800 274 1000" label="0800 274 1000" />
            </Text>
          </div>
          <S.IlustracaoContempladoContent>
            <S.IlustracaoContempladoSm as={IlustracaoContempladoSm} />
            <S.IlustracaoContempladoLg as={IlustracaoContempladoLg} />
          </S.IlustracaoContempladoContent>
        </S.ContentContemplado>
      ) : (
        <S.ContentNaoContemplado>
          <Headline nivel="5" text="Seu título não foi contemplado :(" />
          <Text textSize="lg">
            Continue acreditando na sorte: você pode ser o próximo. Mantenha as
            mensalidades em dia para continuar concorrendo!
          </Text>
        </S.ContentNaoContemplado>
      ),
    [contemplado, tituloBuscado]
  );

  return (
    <Loading loading={loading}>
      {resultados && tituloBuscado ? (
        <S.StatusContemplacao contemplado={tituloBuscado && contemplado}>
          <S.StatuContempladoContent as={SectionContent}>
            {statusContemplacaoContent}
          </S.StatuContempladoContent>
        </S.StatusContemplacao>
      ) : null}
      <SectionContent>
        <ResultadoDeSorteiosForm
          {...props}
          form={FormConsulta}
          buscarResultado={buscarResultadoHandle}
          resultados={resultados}
          dataSorteios={dataSorteios}
          plano={plano}
          onResetForm={() =>
            setBusca({ plano: null, titulo: "", dataBusca: "" })
          }
        />
      </SectionContent>
    </Loading>
  );
};

ResultadoSorteiosCapitalizacao.propTypes = {
  products: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default ResultadoSorteiosCapitalizacao;
